import React, { useMemo, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  List as ListIcon,
} from "@material-ui/icons";
import DialogTitle from "../../../components/dialog-title";
import {
  createSpecialId,
  getDescendantIds,
  moveSpecialRow,
  ROOT_PARENT_ID,
} from "./helpers";

const emptyForm = {
  title: "",
  href: "",
};

export default function ChildrenModalContent(props) {
  const { close, data, allSpecials = [], onSave } = props;
  const [rows, setRows] = useState(allSpecials);
  const [parentStack, setParentStack] = useState([data]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const currentParent = parentStack[parentStack.length - 1];
  const currentChildren = useMemo(
    () => rows.filter((item) => item.parentId === currentParent.uid),
    [rows, currentParent.uid],
  );
  const canCreateChild = currentParent.level < 2;

  const updateRows = (nextRows) => {
    setRows(nextRows);
    onSave(nextRows);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const saveChild = () => {
    if (!form.title?.trim()) return;

    if (editingId) {
      updateRows(
        rows.map((item) =>
          item.id === editingId
            ? { ...item, title: form.title, href: form.href }
            : item,
        ),
      );
    } else {
      const uid = createSpecialId();
      updateRows([
        ...rows,
        {
          uid,
          id: uid,
          title: form.title,
          href: form.href,
          parentId: currentParent.uid || ROOT_PARENT_ID,
          level: currentParent.level + 1,
        },
      ]);
    }

    resetForm();
  };

  const editChild = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      href: item.href || "",
    });
  };

  const deleteChild = (item) => {
    const descendantIds = getDescendantIds(rows, item.id);
    updateRows(
      rows.filter((row) => row.id !== item.id && !descendantIds.has(row.id)),
    );
    if (editingId === item.id) resetForm();
  };

  const moveChild = (item, direction) => {
    updateRows(moveSpecialRow(rows, item.id, direction));
  };

  const openChildren = (item) => {
    if (item.level >= 2) return;
    setParentStack((stack) => [...stack, item]);
    resetForm();
  };

  const goBack = () => {
    if (parentStack.length === 1) return;
    setParentStack((stack) => stack.slice(0, -1));
    resetForm();
  };

  return (
    <>
      <DialogTitle onClose={() => close(true)}>
        <div className="d-flex align-items-center">
          {parentStack.length > 1 && (
            <IconButton size="small" onClick={goBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <span>زیرمجموعه‌های {currentParent.title}</span>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {canCreateChild ? (
          <div className="mb-3">
            <Typography variant="subtitle1">
              {editingId ? "ویرایش بچه" : "ایجاد بچه"}
            </Typography>
            <TextField
              label="عنوان"
              value={form.title}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, title: event.target.value }))
              }
              margin="normal"
              variant="outlined"
              fullWidth
            />
            <TextField
              label="لینک"
              value={form.href}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, href: event.target.value }))
              }
              margin="normal"
              variant="outlined"
              fullWidth
            />
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={saveChild}
            >
              {editingId ? "ذخیره تغییرات" : "افزودن بچه"}
            </Button>
            {editingId && (
              <Button className="mr-2" onClick={resetForm}>
                انصراف
              </Button>
            )}
          </div>
        ) : (
          <Typography className="mb-3" color="textSecondary">
            این آیتم در سطح سوم است و نمی‌تواند زیرمجموعه جدید داشته باشد.
          </Typography>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>عنوان</TableCell>
              <TableCell>لینک</TableCell>
              <TableCell align="center">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentChildren.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{item.title}</TableCell>
                <TableCell>{item.href}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => moveChild(item, -1)}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === currentChildren.length - 1}
                    onClick={() => moveChild(item, 1)}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => editChild(item)}>
                    <EditIcon />
                  </IconButton>
                  {item.level < 2 && (
                    <IconButton size="small" onClick={() => openChildren(item)}>
                      <ListIcon />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => deleteChild(item)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!currentChildren.length && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  زیرمجموعه‌ای ثبت نشده است.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(true)} color="primary">
          بستن
        </Button>
      </DialogActions>
    </>
  );
}
